import React from "react";
import { Link, graphql, useStaticQuery } from "gatsby";
import { Mousewheel, Navigation } from "swiper";
import { Swiper, SwiperSlide } from "swiper/react";
import { motion } from "framer-motion";
import Card from "components/others/card";

const FigmaSection: React.FC = () => {
  const themeItems = useStaticQuery(graphql`
    query FigmaTemplateThemes {
      allMdx(
        filter: { frontmatter: { tags: { in: "Figma", ne: "Shopify" } } }
        sort: { frontmatter: { updatedAt: DESC } }
      ) {
        edges {
          node {
            frontmatter {
              author
              tags
              featuredImage {
                childImageSharp {
                  fluid(maxWidth: 600) {
                    src
                  }
                }
              }
              slug
              previewUrl
              purchaseInfo {
                url
                directLink
              }
              technologies {
                title
                class
              }
              title
              price
              comparePrice
              gumroadId
              ribbonContent
              mainTechnology
              downloadDetails {
                source
                url
                branch
              }
            }
          }
        }
      }
    }
  `);

  const figmaItems = themeItems.allMdx.edges;
  const figmaOnlyItems = figmaItems.filter(
    (item: any) => item.node.frontmatter.tags.length === 1
  );

  return (
    <section className="mb-20 bg-primary-50 py-28">

      <div className="container mx-auto">
        <div className="grid grid-cols-12 lg:gap-8">
          <div className="flex justify-center col-span-12 mb-8 lg:col-span-3 lg:mb-0">
            <div className="flex flex-col items-center text-center lg:items-start lg:text-left">
              <motion.h2 
                className="mb-2.5 text-3xl font-bold text-gray-900 lg:text-4xl"
                initial={{ opacity: 0, y: -20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6 }}
              >
                <motion.span
                  className="inline-block"
                  initial={{ opacity: 0, x: -20 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ duration: 0.6, delay: 0.2 }}
                >
                  Exclusive
                </motion.span>{" "}
                <motion.span
                  className="inline-block text-primary-600"
                  initial={{ opacity: 0, x: 20 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ duration: 0.6, delay: 0.4 }}
                >
                  Figma Themes
                </motion.span>
              </motion.h2>
              <motion.p 
                className="mb-6 text-lg text-neutral-600"
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: 0.6 }}
              >
                Elevate your designs with Figma-only themes
              </motion.p>
              <motion.div
                initial={{ opacity: 0, scale: 0.9 }}
                animate={{ opacity: 1, scale: 1 }}
                transition={{ duration: 0.6, delay: 0.8 }}
                whileHover={{ scale: 1.05 }}
              >
                <Link 
                  to="/tag/figma/" 
                  className="btn-primary-outline"
                >
                  View all themes
                </Link>
              </motion.div>
            </div>
          </div>

          <div className="relative col-span-12 lg:col-span-9">
            <Swiper
              direction={"horizontal"}
              mousewheel={{ forceToAxis: true }}
              loop={true}
              spaceBetween={24}
              breakpoints={{
                0: { slidesPerView: 1 },
                640: { slidesPerView: 1.6 },
                1024: { slidesPerView: 2 },
                1280: { slidesPerView: 2.4 },
              }}
              navigation={{
                nextEl: ".swiper-button-next",
                prevEl: ".swiper-button-prev",
              }}
              modules={[Mousewheel, Navigation]}
            >
              {figmaOnlyItems.map((item: any, index: number) => {
                const cardContent = {
                  name: item.node.frontmatter.title,
                  featuredImage: item.node.frontmatter.featuredImage.childImageSharp.fluid.src,
                  technologies: item.node.frontmatter.technologies,
                  author: item.node.frontmatter.author,
                  type: item.node.frontmatter.type,
                  price: item.node.frontmatter.price,
                  comparePrice: item.node.frontmatter.comparePrice,
                  url: item.node.frontmatter.slug,
                  previewUrl: item.node.frontmatter.previewUrl,
                  purchaseInfo: item.node.frontmatter.purchaseInfo,
                  title: item.node.frontmatter.title,
                  gumroadId: item.node.frontmatter.gumroadId,
                  downloadDetails: item.node.frontmatter.downloadDetails,
                  mainTechnology: item.node.frontmatter.mainTechnology,
                  ribbonContent: item.node.frontmatter.ribbonContent,
                };

                return (
                  <SwiperSlide key={index}>
                    <div className="swiper-card">
                      <Card {...cardContent} />
                    </div>
                  </SwiperSlide>
                );
              })}
            </Swiper>
            <button className="absolute left-0 z-10 p-3 transition-all duration-300 transform -translate-y-1/2 bg-white rounded-full shadow-md swiper-button-prev top-1/2 hover:bg-primary-100">
              <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path d="M15 19L8 12L15 5" stroke="#0474e0" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
              </svg>
            </button>
            <button className="absolute right-0 z-10 p-3 transition-all duration-300 transform -translate-y-1/2 bg-white rounded-full shadow-md swiper-button-next top-1/2 hover:bg-primary-100">
              <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path d="M9 5L16 12L9 19" stroke="#0474e0" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
              </svg>
            </button>
          </div>
        </div>
      </div>
    </section>
  );
};

export default FigmaSection;
